import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Camera, Ruler, Target, BarChart3 } from 'lucide-react';
import * as planService from '../../../services/planService';
import PhotoGallery from './PhotoGallery';
import MeasurementTracker from './MeasurementTracker';
import CheckInForm from './CheckInForm';
import AchievementBadges from './AchievementBadges';
import ProgressChart from '../../shared/ProgressChart';

/**
 * Progress Dashboard Component
 * Overview with charts, progress photos, and adherence metrics
 */
export default function ProgressDashboard({ planId }) {
  const [progress, setProgress] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'photos', 'measurements', 'checkins'

  useEffect(() => {
    if (planId) {
      loadProgress();
    }
  }, [planId]);

  const loadProgress = async () => {
    setIsLoading(true);
    try {
      const result = await planService.getProgress(planId);
      if (result.success) {
        setProgress(result.data);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading progress...</p>
        </div>
      </div>
    );
  }

  const entries = progress?.entries || [];
  const summary = progress?.summary || {};

  // Calculate adherence metrics
  const workoutEntries = entries.filter(e => e.entry_type === 'workout');
  const measurementEntries = entries.filter(e => e.entry_type === 'measurement');
  const photoEntries = entries.filter(e => e.entry_type === 'photo');

  // Get weight data for chart
  const weightData = entries
    .filter(e => e.entry_type === 'weight' && e.data?.weight)
    .map(e => ({
      date: e.entry_date,
      value: e.data.weight,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Progress Tracking</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView('overview')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeView === 'overview'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveView('photos')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeView === 'photos'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Photos
          </button>
          <button
            onClick={() => setActiveView('measurements')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeView === 'measurements'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Measurements
          </button>
          <button
            onClick={() => setActiveView('checkins')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeView === 'checkins'
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Check-Ins
          </button>
        </div>
      </div>

      {activeView === 'overview' && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Total Entries</h3>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{summary.totalEntries || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Progress tracked</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Workouts</h3>
                <BarChart3 className="w-8 h-8 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{summary.workouts || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Workouts logged</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-purple-500"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Measurements</h3>
                <Ruler className="w-8 h-8 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{summary.measurements || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Body measurements</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-yellow-500"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Photos</h3>
                <Camera className="w-8 h-8 text-yellow-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{summary.photos || 0}</p>
              <p className="text-sm text-gray-500 mt-2">Progress photos</p>
            </motion.div>
          </div>

          {/* Weight Chart */}
          {weightData.length > 0 && (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Weight Progress</h3>
              <ProgressChart
                data={weightData}
                type="line"
                xKey="date"
                yKey="value"
                label="Weight (lbs)"
              />
            </div>
          )}

          {/* Achievement Badges */}
          <AchievementBadges planId={planId} entries={entries} />

          {/* Recent Entries */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h3>
            {entries.length > 0 ? (
              <div className="space-y-3">
                {entries.slice(0, 5).map((entry) => (
                  <div key={entry.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                      {entry.entry_type === 'workout' && <BarChart3 className="w-5 h-5 text-primary-600" />}
                      {entry.entry_type === 'measurement' && <Ruler className="w-5 h-5 text-primary-600" />}
                      {entry.entry_type === 'photo' && <Camera className="w-5 h-5 text-primary-600" />}
                      {entry.entry_type === 'weight' && <Target className="w-5 h-5 text-primary-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 capitalize">
                        {entry.entry_type.replace('_', ' ')}
                      </p>
                      <p className="text-sm text-gray-600">
                        {new Date(entry.entry_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 text-center py-4">No progress entries yet</p>
            )}
          </div>
        </>
      )}

      {activeView === 'photos' && <PhotoGallery planId={planId} />}
      {activeView === 'measurements' && <MeasurementTracker planId={planId} />}
      {activeView === 'checkins' && <CheckInForm planId={planId} />}
    </div>
  );
}
